import { useState, useCallback, useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import type { ITableProps } from "@similarweb/pro-logic-components";

import type { ISwitcherConfig } from "app/components/React/WidgetContainer/types";
import type { IFooterButton } from "app/components/React/WidgetContainer/WidgetFooter";
import { allTrackers } from "app/services/track/track";

import useTrafficSearchApiParams from "../../../hooks/useApiParamsSearch";

import { getTopPaidColumns } from "./../tableColumns";
import { switcherConfigItems, getFooterButtonConfig } from "./../utils";
import { useTopPaidData } from "./useTopPaidData";

interface IUseTopLandingPages {
    switcherConfig: ISwitcherConfig;
    tableProps: ITableProps;
    footerButtonsConfig: IFooterButton[];
}

enum ETopPaidLandingPagesSwitcherItems {
    "TopPages" = 0,
    "TopKeywords" = 1,
    "New" = 2,
}

export const useTopPaidLandingPages = (): IUseTopLandingPages => {
    // Manage switcher state
    const [selectedSwitcherItem, setSelectedSwitcherItem] = useState<ETopPaidLandingPagesSwitcherItems>(
        ETopPaidLandingPagesSwitcherItems.TopPages,
    );
    const handleSwitcherItemClick = useCallback((index: number) => {
        setSelectedSwitcherItem(index);
        allTrackers.dispatchTrackEvent("Tab", "click", `Top paid landing pages/${switcherConfigItems[index].value}`);
    }, []);

    const switcherConfig = useMemo<ISwitcherConfig>(
        () => ({
            items: switcherConfigItems,
            initialSelectedItem: selectedSwitcherItem,
            onItemClick: handleSwitcherItemClick,
        }),
        [selectedSwitcherItem],
    );

    // Get router params and compute API params
    const { params } = useCurrentStateAndParams();
    const { trafficSearchApiParams } = useTrafficSearchApiParams();
    const apiParams = useMemo(
        () => ({
            ...trafficSearchApiParams,
            sourceType: "Paid",
            ChangeType: switcherConfigItems[selectedSwitcherItem].landingPagesChangeFilter,
        }),
        [selectedSwitcherItem, trafficSearchApiParams],
    );

    // Fetch data and memoize table columns
    const { data = [], isLoading } = useTopPaidData(apiParams);
    const isNewTabSwitcherSelected = selectedSwitcherItem === ETopPaidLandingPagesSwitcherItems.New;
    const columnConfig = useMemo(
        () => (data.length > 0 && !isLoading ? getTopPaidColumns(isNewTabSwitcherSelected) : []),
        [data, isLoading],
    );

    // Prepare table props
    const tableProps = useMemo(
        () => ({
            data,
            rowHoverable: true,
            isLoading,
            columnConfig,
        }),
        [data, isLoading, columnConfig],
    );

    // Prepare footer buttons configuration
    const footerButtonsConfig = useMemo(
        () =>
            getFooterButtonConfig({
                ...params,
                selectedPageTab: "Paid",
                Change: switcherConfigItems[selectedSwitcherItem].landingPagesChangeFilter,
            }),
        [params, selectedSwitcherItem],
    );

    return { switcherConfig, tableProps, footerButtonsConfig };
};
